(() => {
  'use strict';

  const GALLERY_SELECTOR =
    'slide-with-thumbs[data-dji-hover-gallery="true"]';
  const LOADER_KEY = '__boyaDjiProductGalleryLoader';
  const MODULE_KEY = '__boyaDjiProductGalleryModule';
  const DESKTOP_MEDIA = window.matchMedia('(min-width: 768px)');
  const observedGalleries = new WeakSet();
  const loaderScript = document.currentScript;
  const moduleSource =
    loaderScript?.dataset.galleryModuleUrl ||
    './dji-product-gallery.js';
  const moduleUrl = new URL(
    moduleSource,
    loaderScript?.src || document.baseURI
  ).href;

  if (window[LOADER_KEY]) {
    window[LOADER_KEY].observe(document);
    return;
  }

  let galleryModulePromise = null;

  function hydrateGalleryThumbnails(gallery) {
    if (!gallery || !DESKTOP_MEDIA.matches) return;

    const thumbnailImages = gallery.querySelectorAll(
      '.dji-gallery__thumbs img[data-src], .dji-gallery__thumbs img[data-srcset]'
    );

    thumbnailImages.forEach((image, index) => {
      const deferredSrcset = image.getAttribute('data-srcset');
      const deferredSrc = image.getAttribute('data-src');

      image.loading = 'eager';
      image.decoding = 'async';
      image.fetchPriority = index === 0 ? 'high' : 'low';

      if (deferredSrcset) {
        image.setAttribute('srcset', deferredSrcset);
        image.removeAttribute('data-srcset');
      }

      if (deferredSrc) {
        image.setAttribute('src', deferredSrc);
        image.removeAttribute('data-src');
      }
    });

    gallery.dataset.djiThumbnailsHydrated = 'true';
  }

  function loadGalleryModule() {
    if (!galleryModulePromise) {
      galleryModulePromise = import(moduleUrl).catch(
        (error) => {
          galleryModulePromise = null;
          console.error('[BOYA] Unable to load the product gallery.', error);
          return null;
        }
      );
    }

    return galleryModulePromise;
  }

  function initialiseLoadedGallery(gallery) {
    return loadGalleryModule().then(() => {
      window[MODULE_KEY]?.initialise(gallery);
    });
  }

  function getGalleries(root) {
    const galleries = [];

    if (root instanceof Element && root.matches(GALLERY_SELECTOR)) {
      galleries.push(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      galleries.push(...root.querySelectorAll(GALLERY_SELECTOR));
    }

    return galleries;
  }

  function observe(root = document) {
    getGalleries(root).forEach((gallery) => {
      hydrateGalleryThumbnails(gallery);

      // Do not trust this data attribute as the source of truth. Translation
      // apps may clone it without cloning the IntersectionObserver/listeners.
      if (observedGalleries.has(gallery)) return;

      observedGalleries.add(gallery);
      gallery.dataset.djiGalleryLoaderObserved = 'true';

      if (!('IntersectionObserver' in window)) {
        initialiseLoadedGallery(gallery);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          observer.disconnect();
          initialiseLoadedGallery(gallery);
        },
        { rootMargin: '600px 0px' }
      );

      observer.observe(gallery);
    });
  }

  window[LOADER_KEY] = {
    observe,
    load: loadGalleryModule,
    moduleUrl
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observe(document), {
      once: true,
    });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
  });

  // Transcy and Shopify section rendering can replace a translated section
  // after DOMContentLoaded. Batch added-node checks into one animation frame;
  // text-only translation mutations do not trigger any gallery work.
  let mutationFrame = 0;
  const pendingRoots = new Set();
  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          if (
            node.matches(GALLERY_SELECTOR) ||
            node.querySelector(GALLERY_SELECTOR)
          ) {
            pendingRoots.add(node);
          }
          return;
        }

        if (
          node instanceof DocumentFragment &&
          node.querySelector(GALLERY_SELECTOR)
        ) {
          pendingRoots.add(node);
        }
      });
    });

    if (mutationFrame || !pendingRoots.size) return;

    mutationFrame = window.requestAnimationFrame(() => {
      mutationFrame = 0;
      pendingRoots.forEach((root) => observe(root));
      pendingRoots.clear();
    });
  });

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  const handleDesktopChange = (event) => {
    if (!event.matches) return;

    getGalleries(document).forEach(hydrateGalleryThumbnails);
  };

  if (typeof DESKTOP_MEDIA.addEventListener === 'function') {
    DESKTOP_MEDIA.addEventListener('change', handleDesktopChange);
  } else if (typeof DESKTOP_MEDIA.addListener === 'function') {
    DESKTOP_MEDIA.addListener(handleDesktopChange);
  }
})();
